import React, { useEffect, useState } from 'react';
import { useFormik, FormikProvider } from 'formik';
import { Table, Checkbox, Alert } from 'antd';
import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash } from '@fortawesome/free-solid-svg-icons';
import { Button, SelectBox } from 'src/components/design-system';
import { TextInput } from 'src/components/design-system/forms';
import { useUploadContextFactory } from './upload-contexts/use-upload-context-factory';
import { LabelsSelector } from 'src/components/labels/labels-selector/labels-selector';
import styles from './data-validator.module.scss';
import { useSelector } from 'react-redux';
import { getLang } from 'shared/store/selectors/lang.selector';
import { Loader } from 'src/components/core';
import { formatNumber } from 'shared/utils';

const DataValidator = ({ contextType }) => {
  const lang = useSelector(getLang('SETTINGS'));
  const {
    mappedData,
    setMappedData,
    validationSchema,
    properties,
    propertyOptions,
    propertiesMap,
    onSubmit,
    hasLocations,
    mandatoryFields = [],
    CustomPropsRender = null,
    canSubmit = true,
  } = useUploadContextFactory({ contextType });

  // Remove location column if there are no locations
  const displayProperties = hasLocations
    ? properties
    : properties.filter(property => property !== 'location');

  const [showOnlyErrors, setShowOnlyErrors] = useState(false);
  const [loading, setLoading] = useState(false);
  const [hadErrors, setHadErrors] = useState(false);

  const formik = useFormik({
    initialValues: { [contextType]: mappedData },
    validationSchema: validationSchema,
    onSubmit: async values => {
      setLoading(true);
      await onSubmit(values);
      setLoading(false);
    },
    validateOnChange: true,
    validateOnBlur: true,
  });

  useEffect(() => {
    formik.validateForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validationSchema]);

  // Sync formik values back to persisted state
  useEffect(() => {
    setMappedData(formik.values[contextType]);
  }, [formik.values, contextType, setMappedData]);

  const removeRow = index => {
    const newData = [...formik.values[contextType]];
    newData.splice(index, 1);
    formik.setFieldValue(contextType, newData);
  };

  // Check if there are any validation errors in the form
  const hasErrors = Boolean(
    formik.errors[contextType] &&
    Array.isArray(formik.errors[contextType]) &&
    formik.errors[contextType].some(err => err && Object.keys(err).length > 0),
  );

  // Track if there were ever errors
  useEffect(() => {
    if (hasErrors) {
      setHadErrors(true);
    }
  }, [hasErrors]);

  // Count total errors for display
  const errorCount = formik.errors[contextType]
    ? formik.errors[contextType].reduce((count, err) => {
        if (err && typeof err === 'object') {
          return count + Object.keys(err).length;
        }
        return count;
      }, 0)
    : 0;

  const columns = [
    {
      title: lang.ACTIONS,
      key: 'actions',
      width: 80,
      fixed: 'left',
      render: (_, record) => (
        <Button
          type="text"
          danger
          icon={<FontAwesomeIcon icon={faTrash} />}
          aria-label={lang.REMOVE_ROW}
          onClick={() => removeRow(record._index)}
          disabled={loading}
        />
      ),
    },
    ...displayProperties.map(property => ({
      title: propertyOptions[property],
      dataIndex: property,
      key: property,
      width: 200,
      render: (_, record) => (
        <FieldRenderer
          contextType={contextType}
          property={property}
          index={record._index}
          formik={formik}
          propertiesMap={propertiesMap}
          mandatoryFields={mandatoryFields}
          loading={loading}
        />
      ),
    })),
  ];

  const dataSource = formik.values[contextType]
    .map((item, index) => ({
      key: index,
      ...item,
      _index: index,
    }))
    .filter((_, index) => {
      if (!showOnlyErrors) return true;
      return formik.errors[contextType] && formik.errors[contextType][index];
    });

  return (
    <div className={styles.container}>
      <FormikProvider value={formik}>
        <form onSubmit={formik.handleSubmit}>
          {mappedData.length > 0 && (
            <div className={styles.validatorContainer}>
              <div className={styles.header}>
                {CustomPropsRender}
                <Button
                  className={styles.submitButton}
                  type="primary"
                  htmlType="submit"
                  size="large"
                  disabled={!mappedData.length || hasErrors || loading || !canSubmit}
                  loading={loading}>
                  Submit ({formatNumber(formik.values[contextType].length)} rows)
                </Button>
                {hasErrors ? (
                  <Alert
                    message={lang.VALIDATION_ERRORS}
                    description={
                      <div>
                        <div>
                          {lang.VALIDATION_ERROR_DESCRIPTION.replace(
                            '{count}',
                            formatNumber(errorCount),
                          )}
                        </div>
                        <Checkbox
                          checked={showOnlyErrors}
                          onChange={e => setShowOnlyErrors(e.target.checked)}
                          className={styles.errorCheckbox}>
                          {lang.SHOW_ONLY_ERRORS}
                        </Checkbox>
                      </div>
                    }
                    type="error"
                    showIcon
                    closable
                  />
                ) : hadErrors ? (
                  <Alert
                    message={lang.ALL_ERRORS_RESOLVED}
                    description={lang.ALL_ERRORS_RESOLVED_DESCRIPTION}
                    type="success"
                    showIcon
                    closable
                  />
                ) : null}
              </div>
              {loading ? (
                <Loader />
              ) : (
                <div>
                  <div className={styles.tableWrapper}>
                    <Table
                      columns={columns}
                      dataSource={dataSource}
                      scroll={{ x: 'max-content' }}
                      pagination={{
                        pageSize: 50,
                        showSizeChanger: true,
                        showTotal: (total, range) =>
                          `${formatNumber(range[0])}-${formatNumber(range[1])} of ${formatNumber(total)} items`,
                      }}
                      rowKey={record => record.id || `${record.email}-${record._index}`}
                      rowClassName={record => {
                        const realIndex = record._index;
                        const hasError =
                          formik.errors[contextType] && formik.errors[contextType][realIndex];
                        return classNames({
                          [styles.errorRow]: hasError,
                        });
                      }}
                    />
                  </div>
                </div>
              )}
            </div>
          )}
        </form>
      </FormikProvider>
    </div>
  );
};

const FieldRenderer = ({
  contextType,
  property,
  index,
  formik,
  propertiesMap,
  mandatoryFields,
  loading,
}) => {
  const lang = useSelector(getLang('SETTINGS'));
  const fieldName = `${contextType}.${index}.${property}`;
  const value = formik.values[contextType][index][property];
  const error = formik.errors[contextType]?.[index]?.[property];

  const handleChange = newValue => {
    formik.setFieldValue(fieldName, newValue);
  };

  const handleBlur = () => {
    formik.setFieldTouched(fieldName, true);
  };

  const handleSelectChange = newValue => {
    formik.setFieldValue(fieldName, newValue);
    formik.setFieldTouched(fieldName, true, false);
  };

  const professionalField = propertiesMap[property];
  const isMandatory = mandatoryFields?.includes(property);

  // Location field with labels selector
  if (property === 'location' && professionalField?.multiple) {
    return (
      <LocationSelectField
        value={value}
        onChange={handleSelectChange}
        error={error}
        disabled={loading}
        fieldName={fieldName}
        lang={lang}
      />
    );
  }

  // Multi-select field
  if (professionalField?.multiple) {
    return (
      <MultiSelectField
        value={value}
        options={professionalField.options}
        onChange={handleSelectChange}
        error={error}
        disabled={loading}
        lang={lang}
      />
    );
  }

  // Single select field
  if (professionalField && !professionalField.multiple) {
    return (
      <SingleSelectField
        value={value}
        options={professionalField.options}
        onChange={handleSelectChange}
        error={error}
        disabled={loading}
        lang={lang}
      />
    );
  }

  // Text input field (default)
  return (
    <TextInput
      value={value || ''}
      onChange={e => handleChange(e.target.value)}
      onBlur={handleBlur}
      error={error || ''}
      disabled={loading}
      size="large"
      allowClear={!isMandatory}
      noMarginBottom
    />
  );
};

const SingleSelectField = ({ value, options, onChange, error, disabled, lang }) => {
  const isValueValid = value ? options.some(opt => opt.value === value) : true;
  const selectValue = isValueValid ? value : '';
  const errorMessage = error || (!isValueValid ? lang.INVALID_VALUE : '');

  return (
    <SelectBox
      value={selectValue}
      onChange={(_, val) => onChange(val)}
      name="singleSelect"
      error={errorMessage}
      disabled={disabled}
      placeholder={lang.SELECT_OPTION}
      options={options}
      noMarginBottom
    />
  );
};

const MultiSelectField = ({ value, options, onChange, error, disabled, lang }) => {
  const validValues = Array.isArray(value)
    ? value.filter(val => options.some(opt => opt.value === val))
    : [];

  return (
    <SelectBox
      mode="multiple"
      value={validValues}
      onChange={(_, val) => onChange(val)}
      name="multiSelect"
      error={error}
      disabled={disabled}
      placeholder={lang.SELECT_OPTIONS}
      options={options}
      noMarginBottom
    />
  );
};

const LocationSelectField = ({ value, onChange, error, disabled, fieldName, lang }) => {
  const validValues = Array.isArray(value) ? value : [];

  const handleChangeWrapper = (_, newValue) => onChange(newValue);

  return (
    <LabelsSelector
      name={fieldName}
      value={validValues}
      onChange={handleChangeWrapper}
      error={error}
      disabled={disabled}
      placeholder={lang.SELECT_LOCATIONS}
      noMarginBottom
    />
  );
};

export default DataValidator;
